import React, { useState, useEffect, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTimer } from '@contexts/TimerContext'
import { Play, Pause } from 'lucide-react'

interface DigitAnimationState {
  digit: string
  shouldAnimate: boolean
}

// 智能時間格式化函數
const formatSmartDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60
  
  if (hours >= 10) {
    return `${hours}h`
  } else if (hours >= 1) {
    return `${hours}:${minutes.toString().padStart(2, '0')}h`
  } else {
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
}

const TimerHeaderWidget: React.FC = () => {
  const { activeTimer, isRunning, elapsedTime } = useTimer()
  const location = useLocation()
  const [prevFormattedTime, setPrevFormattedTime] = useState('')
  const [isVisible, setIsVisible] = useState(false)

  // 計算格式化時間 - 使用智能格式
  const formattedTime = useMemo(() => {
    return formatSmartDuration(elapsedTime)
  }, [elapsedTime])

  // 計算需要動畫的數字
  const animationStates = useMemo((): DigitAnimationState[] => {
    const newDigits = formattedTime.split('')
    const oldDigits = prevFormattedTime.split('')
    
    return newDigits.map((digit, index) => ({
      digit,
      shouldAnimate: digit !== oldDigits[index] && oldDigits.length > 0
    }))
  }, [formattedTime, prevFormattedTime])

  // 檢測重要時刻變化 - 適應新的智能格式
  const isImportantChange = useMemo(() => {
    if (!prevFormattedTime) return false
    
    const prevSeconds = elapsedTime - 1
    const currSeconds = elapsedTime
    
    // 檢測小時變化（從59:59到1:00h，或從9:59h到10h）
    const prevHours = Math.floor(prevSeconds / 3600)
    const currHours = Math.floor(currSeconds / 3600)
    
    if (prevHours !== currHours) return 'hour'
    
    // 檢測分鐘變化（只在小時數相同時檢查）
    const prevMinutes = Math.floor((prevSeconds % 3600) / 60)
    const currMinutes = Math.floor((currSeconds % 3600) / 60)
    
    if (prevMinutes !== currMinutes) return 'minute'
    
    return false
  }, [elapsedTime, prevFormattedTime])

  // 更新前一次時間
  useEffect(() => {
    if (formattedTime !== prevFormattedTime) {
      setPrevFormattedTime(formattedTime)
    }
  }, [formattedTime, prevFormattedTime])

  // 控制組件可見性
  useEffect(() => {
    setIsVisible(!!activeTimer)
  }, [activeTimer])

  // 如果沒有活動計時器，不顯示組件
  if (!activeTimer || !isVisible) {
    return null
  }

  return (
    <Link
      to="/timer"
      className={`
        inline-flex items-center space-x-1 sm:space-x-2 md:space-x-3 
        px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-2.5
        rounded-lg border transition-all duration-300 cursor-pointer
        hover:scale-105 hover:shadow-medium flex-shrink-0
        ${isRunning 
          ? 'bg-success-50 border-success-200 shadow-soft hover:bg-success-100' 
          : 'bg-secondary-50 border-secondary-200 shadow-soft hover:bg-secondary-100'
        }
        ${location.pathname === '/timer' ? 'ring-2 ring-primary-300 ring-opacity-50' : ''}
        animate-fade-in
      `}
    >
      {/* 狀態指示器 */}
      <div className="relative flex items-center flex-shrink-0">
        <div className={`
          w-1.5 h-1.5 sm:w-2 sm:h-2 md:w-2.5 md:h-2.5 rounded-full transition-all duration-300
          ${isRunning ? 'bg-success-500' : 'bg-secondary-400'}
        `} />
        {isRunning && (
          <>
            {/* 呼吸燈效果 */}
            <div className="absolute inset-0 w-1.5 h-1.5 sm:w-2 sm:h-2 md:w-2.5 md:h-2.5 bg-success-400 rounded-full animate-ping opacity-75" />
            <div className="absolute inset-0 w-1.5 h-1.5 sm:w-2 sm:h-2 md:w-2.5 md:h-2.5 bg-success-500 rounded-full animate-pulse" />
          </>
        )}
        {/* 播放/暫停圖示 - 只在較大螢幕顯示 */}
        <div className="hidden sm:block ml-0.5 md:ml-1">
          {isRunning ? (
            <Play className="w-2.5 h-2.5 md:w-3 md:h-3 text-success-600" fill="currentColor" />
          ) : (
            <Pause className="w-2.5 h-2.5 md:w-3 md:h-3 text-secondary-500" />
          )}
        </div>
      </div>

      {/* 時間顯示區域 */}
      <div className="relative flex-shrink-0">
        <div className={`
          font-mono font-semibold text-xs sm:text-sm md:text-base tracking-tight sm:tracking-wider
          transition-colors duration-300
          ${isRunning ? 'text-success-600' : 'text-secondary-600'}
        `}
        style={{ fontVariantNumeric: 'tabular-nums' }}>
          {animationStates.map((state, index) => (
            <span
              key={`${index}-${state.digit}`}
              className={`
                inline-block transition-all duration-200 ease-out
                ${state.shouldAnimate ? 'animate-digit-flip' : ''}
                ${isImportantChange === 'hour' && state.shouldAnimate ? 'animate-pulse' : ''}
                ${isImportantChange === 'minute' && state.shouldAnimate ? 'animate-bounce' : ''}
              `}
              style={{
                textShadow: state.shouldAnimate && isRunning 
                  ? '0 0 8px rgba(34, 197, 94, 0.4)' 
                  : 'none',
                transform: state.shouldAnimate ? 'scale(1.05)' : 'scale(1)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              {state.digit}
            </span>
          ))}
        </div>
        
        {/* 重要時刻的光暈效果 */}
        {isImportantChange && isRunning && (
          <div className={`
            absolute inset-0 rounded opacity-30 animate-pulse
            ${isImportantChange === 'hour' 
              ? 'bg-primary-200 shadow-glow' 
              : 'bg-success-200'
            }
          `} />
        )}
      </div>
    </Link>
  )
}

export default TimerHeaderWidget
